import { isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useCheckCashByTradeCapture } from '@/App/tiles/CashTile/CashContext.tsx';
import { useDisabledDatePresets } from '@/App/tiles/shared/useDisabledDatePresets.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { cashNotionalCurrencyChanged } from '@/store/cash/slice.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

export type CashTileMainFieldsProps = {
  tileId: string;
};

/**
 * CashTileMainFields displays and manages the main input fields for Cash FX trades (Spot/Forward/NDF).
 * Handles notional input, currency toggle, and maturity date selection.
 * Works for all cash product types (FxSpot, FxFwd, FxNdf).
 *
 * @example
 * <CashTileMainFields tileId="tile-123" />
 */
export function CashTileMainFields(props: CashTileMainFieldsProps) {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const {
    productType,
    currencyPair,
    notional,
    notionalCurrencyIndex,
    notionalCurrency,
    maturityDate,
    maturityDateTenor,
    tradeCapture,
    errors,
  } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);

    if (isNotDefined(tile)) {
      return {};
    }

    return {
      productType: tile.type,
      currencyPair: tile.properties.currencyPair,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      notionalCurrency: isDefined(tile.properties.notionalCurrencyIndex)
        ? tile.properties.notionalCurrencyIndex === 1
          ? tile.properties.currency1
          : tile.properties.currency2
        : undefined,
      maturityDate: tile.properties.maturityDate ?? '',
      maturityDateTenor: tile.properties.maturityDateTenor,
      tradeCapture: tile.tradeCapture,
      errors: tile.tradeCapture && 'errors' in tile.tradeCapture ? tile.tradeCapture.errors : undefined,
    };
  }, deepEqual);

  const disabledDatePresets = useDisabledDatePresets(tileId, currencyPair);

  const checkCashByTradeCapture = useCheckCashByTradeCapture();

  const handleNotionalChange = useCallback(
    (value: string | number | undefined) => {
      if (isNotDefined(tradeCapture) || isNotDefined(productType)) {
        return;
      }

      sgwtWebAnalytics?.trackEvent('Cash', 'Notional changed', String(value));

      const notionalValue = typeof value === 'number' ? value : isDefined(value) ? Number(value) : undefined;

      checkCashByTradeCapture({
        notional: notionalValue,
        notionalCurrencyIndex,
      });
    },
    [productType, tradeCapture, notionalCurrencyIndex, checkCashByTradeCapture, sgwtWebAnalytics],
  );

  const handleToggleNotionalCurrency = useCallback(() => {
    if (isNotDefined(tradeCapture) || isNotDefined(productType)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Cash', 'Notional currency changed', notionalCurrencyIndex === 1 ? '2' : '1');

    const newCurrencyIndex = notionalCurrencyIndex === 1 ? 2 : 1;

    if (isNotDefined(notional) || notional === 0) {
      dispatch(
        cashNotionalCurrencyChanged({
          tileId,
          notionalCurrencyIndex: newCurrencyIndex,
        }),
      );

      return;
    }

    checkCashByTradeCapture({
      notional,
      notionalCurrencyIndex: newCurrencyIndex,
    });
  }, [tileId, productType, tradeCapture, notionalCurrencyIndex, notional, checkCashByTradeCapture, sgwtWebAnalytics]);

  const handleMaturityDateChange = useCallback(
    (dateOrTenor: string) => {
      if (isNotDefined(tradeCapture) || isNotDefined(productType)) {
        return;
      }

      sgwtWebAnalytics?.trackEvent('Cash', 'Maturity changed', dateOrTenor);

      checkCashByTradeCapture({
        maturityDate: dateOrTenor,
      });
    },
    [productType, tradeCapture, checkCashByTradeCapture, sgwtWebAnalytics],
  );

  const notionalErrors = errors?.notional ?? [];
  const maturityErrors = errors?.maturityDate ?? [];

  return (
    <div className="input-group input-group-sm p-2">
      <div className="form-group row my-1 no-gutters">
        <div className="col-6 pe-1">
          <TradeCaptureValidationWrapper errors={notionalErrors}>
            <SgNumberInput
              size="sm"
              value={notional}
              valuePosition="start"
              maxDecimals={9}
              selectAllOnFocus
              acceleratorsEnabled
              allowNegative={false}
              placeholder={formatMessage({ id: 'CashTile.SpotMainFields.notional' })}
              onChange={handleNotionalChange}
              unit={notionalCurrency}
              unitBackgroundColor={notionalCurrencyIndex === 1 ? undefined : 'warning'}
              unitPosition="end"
              unitTabNoIndex
              onUnitClick={handleToggleNotionalCurrency}
              data-e2e="notional"
              state={notionalErrors.length > 0 ? 'invalid' : undefined}
            />
          </TradeCaptureValidationWrapper>
        </div>

        <div className="col-6 ps-1">
          <TradeCaptureValidationWrapper errors={maturityErrors}>
            <DatePickerWithTenorNext
              date={maturityDate}
              placeholder={formatMessage({ id: 'CashTile.SpotMainFields.maturityDate' })}
              tenor={maturityDateTenor as string}
              onChange={handleMaturityDateChange}
              data-e2e="maturity-picker"
              validationState={maturityErrors.length > 0 ? 'invalid' : undefined}
              disablePresets={disabledDatePresets}
            />
          </TradeCaptureValidationWrapper>
        </div>
      </div>
    </div>
  );
}
